import {Chart, Plugin} from 'chart.js';

export const MAX_PERSISTENT_COMPARISON_CONNECTIONS = 25;

/**
 * Keeps portfolio comparisons readable without hiding data points.
 * All dots remain visible. Persistent connection lines are only drawn for one bounded organisation;
 * larger or mixed portfolios get one connection on hover instead.
 */
export class EnergyCompassYearComparisonPlugin implements Plugin<'scatter'> {
    id = 'energyCompassYearComparison';
    private readonly chartStates = new WeakMap<Chart<'scatter'>, ComparisonChartState>();

    beforeDatasetsDraw(chart: Chart<'scatter'>): void {
        const state = this.ensureState(chart);
        const hoveredConnection = state.hoveredLocationId == null ? null
            : state.connectionsByLocation.get(state.hoveredLocationId);
        if (hoveredConnection) {
            chart.ctx.save();
            chart.ctx.globalAlpha = 0.16;
            state.datasetsFaded = true;
            return;
        }
        if (state.showPersistentConnections) {
            this.drawConnections(chart, state.connections, 0.32);
        }
    }

    afterDatasetsDraw(chart: Chart<'scatter'>): void {
        const state = this.ensureState(chart);
        if (!state.datasetsFaded) return;
        chart.ctx.restore();
        state.datasetsFaded = false;

        const connection = state.hoveredLocationId == null ? null
            : state.connectionsByLocation.get(state.hoveredLocationId);
        if (!connection) return;
        this.drawConnections(chart, [connection], 0.72);
        connection.from.draw?.(chart.ctx, chart.chartArea);
        connection.to.draw?.(chart.ctx, chart.chartArea);
    }

    afterEvent(chart: Chart<'scatter'>, args: any): void {
        const state = this.ensureState(chart);
        const interactionEvent = args.event?.native || args.event;
        const active = chart.getElementsAtEventForMode(
            interactionEvent as Event, 'nearest', {intersect: true}, false)[0] as any;
        const point = active == null ? null
            : (chart.data.datasets[active.datasetIndex] as any)?.data?.[active.index] as ComparisonPoint | undefined;
        const hoveredLocationId = point?.locationId && state.connectionsByLocation.has(point.locationId)
            ? point.locationId : null;
        const hoveredConnection = hoveredLocationId == null ? null
            : state.connectionsByLocation.get(hoveredLocationId);
        if (hoveredConnection) {
            chart.tooltip?.setActiveElements([
                hoveredConnection.fromIndex,
                hoveredConnection.toIndex
            ], {
                x: args.event?.x ?? active?.element?.x ?? 0,
                y: args.event?.y ?? active?.element?.y ?? 0
            });
        }
        if (hoveredLocationId === state.hoveredLocationId) return;
        state.hoveredLocationId = hoveredLocationId;
        args.changed = true;
    }

    afterDestroy(chart: Chart<'scatter'>): void {
        this.chartStates.delete(chart);
    }

    private ensureState(chart: Chart<'scatter'>): ComparisonChartState {
        let state = this.chartStates.get(chart);
        const snapshots = chart.data.datasets.map((dataset, index) => ({
            data: dataset.data,
            visible: chart.isDatasetVisible(index)
        }));
        if (state && sameSnapshots(state.snapshots, snapshots)) return state;

        const connections = this.buildConnections(chart);
        const organisationIds = new Set(connections.map(connection => connection.organisationId).filter(Boolean));
        state = {
            snapshots,
            connections,
            connectionsByLocation: new Map(connections.map(connection => [connection.locationId, connection])),
            showPersistentConnections: connections.length > 0
                && connections.length <= MAX_PERSISTENT_COMPARISON_CONNECTIONS
                && connections.every(connection => !!connection.organisationId)
                && organisationIds.size === 1,
            hoveredLocationId: state?.hoveredLocationId || null,
            datasetsFaded: false
        };
        this.chartStates.set(chart, state);
        return state;
    }

    private buildConnections(chart: Chart<'scatter'>): ComparisonConnection[] {
        const comparisonPointsByAxis = new Map<string, Map<string, IndexedPoint>>();
        chart.data.datasets.forEach((dataset: any, datasetIndex) => {
            if (dataset.comparisonYear !== true || !chart.isDatasetVisible(datasetIndex)) return;
            const points = comparisonPointsByAxis.get(dataset.yAxisID) || new Map<string, IndexedPoint>();
            const elements = chart.getDatasetMeta(datasetIndex).data;
            dataset.data?.forEach((point: ComparisonPoint, pointIndex: number) => {
                if (!point.locationId) return;
                points.set(point.locationId, {
                    element: elements[pointIndex] as PositionedPoint,
                    point,
                    activeIndex: {datasetIndex, index: pointIndex}
                });
            });
            comparisonPointsByAxis.set(dataset.yAxisID, points);
        });

        const connections: ComparisonConnection[] = [];
        chart.data.datasets.forEach((dataset: any, datasetIndex) => {
            if (dataset.comparisonYear === true || !chart.isDatasetVisible(datasetIndex)) return;
            const comparisonPoints = comparisonPointsByAxis.get(dataset.yAxisID);
            if (!comparisonPoints) return;
            const elements = chart.getDatasetMeta(datasetIndex).data;
            dataset.data?.forEach((point: ComparisonPoint, pointIndex: number) => {
                if (!point.locationId) return;
                const comparison = comparisonPoints.get(point.locationId);
                const from = elements[pointIndex] as PositionedPoint;
                if (!comparison?.element || !from) return;
                connections.push({
                    locationId: point.locationId,
                    organisationId: point.organisationId || comparison.point.organisationId,
                    from,
                    to: comparison.element,
                    fromIndex: {datasetIndex, index: pointIndex},
                    toIndex: comparison.activeIndex,
                    color: dataset.pointBorderColor || dataset.backgroundColor || '#6c757d'
                });
            });
        });
        return connections;
    }

    private drawConnections(chart: Chart<'scatter'>, connections: ComparisonConnection[], alpha: number): void {
        const context = chart.ctx;
        context.save();
        context.globalAlpha = alpha;
        context.lineWidth = 1;
        context.setLineDash([]);
        connections.forEach(connection => {
            context.beginPath();
            context.moveTo(connection.from.x, connection.from.y);
            context.lineTo(connection.to.x, connection.to.y);
            context.strokeStyle = connection.color;
            context.stroke();
        });
        context.restore();
    }
}

interface ComparisonPoint {
    locationId?: string;
    organisationId?: string;
}

interface PositionedPoint {
    x: number;
    y: number;
    draw?: (context: CanvasRenderingContext2D, chartArea?: any) => void;
}

interface IndexedPoint {
    element: PositionedPoint;
    point: ComparisonPoint;
    activeIndex: ActivePointIndex;
}

interface ActivePointIndex {
    datasetIndex: number;
    index: number;
}

interface ComparisonConnection {
    locationId: string;
    organisationId?: string;
    from: PositionedPoint;
    to: PositionedPoint;
    fromIndex: ActivePointIndex;
    toIndex: ActivePointIndex;
    color: any;
}

interface DatasetSnapshot {
    data: unknown;
    visible: boolean;
}

interface ComparisonChartState {
    snapshots: DatasetSnapshot[];
    connections: ComparisonConnection[];
    connectionsByLocation: Map<string, ComparisonConnection>;
    showPersistentConnections: boolean;
    hoveredLocationId: string | null;
    datasetsFaded: boolean;
}

function sameSnapshots(left: DatasetSnapshot[], right: DatasetSnapshot[]): boolean {
    return left.length === right.length
        && left.every((snapshot, index) => snapshot.data === right[index].data
            && snapshot.visible === right[index].visible);
}
